import { useMemo } from "react";
import { BUNDESLAND_VORGABE, feiertagAm } from "@mrmachine/fach";
import type { Bundesland, Feiertag } from "@mrmachine/fach";
import { useT } from "../i18n";
import { useSitzung } from "../schale/Sitzung";
import { tagImBetrieb } from "../daten/betriebstag";
import { feiertageZwischen, feiertagName, feiertagssatz } from "../daten/feiertage";
import "../stile/feiertage.css";

/* ══════════════════════════════════════════════════════════════════
   Feiertage — ein Hinweis, keine Rechnung (08.10.2026, 0287)

   Luca: *„Feiertage zählen weiter als Miettag. … bereite das vor, dass bei
   der Angebotsstellung oder bei Schnellvermietungen, wenn es einen
   Feiertag dazwischen gibt, das angezeigt wird."*

   Das Land kommt aus `GET /ich` (eingestellt, sonst aus der Postleitzahl
   der Firmenanschrift, sonst Niedersachsen) — jeder, der vermietet oder
   plant, bekommt es, auch ohne das Recht „verwalten". Die Tage rechnet
   `@mrmachine/fach` hier in der Oberfläche.
   ══════════════════════════════════════════════════════════════════ */

/** Das Land, dessen Feiertage gezeigt werden. */
export function useFeiertagsland(): Bundesland {
  /* Ohne Sitzung darüber (ein Baustein im Test) gilt die Vorgabe. Der
     Haken ruft `useContext` in jedem Fall — die Reihenfolge bleibt gleich. */
  try {
    return useSitzung().ich?.feiertagsland ?? BUNDESLAND_VORGABE;
  } catch {
    return BUNDESLAND_VORGABE;
  }
}

/** Ist dieser Tag ein Feiertag? Dann sein Name — für die Spalten von Kalender, Einteilen und Tagesleiste. */
export function useFeiertagAm(): (tag: string) => string | null {
  const t = useT();
  const land = useFeiertagsland();
  return useMemo(() => {
    const merk = new Map<string, Feiertag | null>();
    return (tag: string) => {
      const iso = tagImBetrieb(tag);
      if (!merk.has(iso)) merk.set(iso, feiertagAm(iso, land));
      const f = iso ? merk.get(iso) : null;
      return f ? feiertagName(t, f) : null;
    };
  }, [land, t]);
}

/** Die Feiertage eines Zeitraums im Land des Betriebs. */
export function useFeiertageIm(von: string | null | undefined, bis: string | null | undefined): Feiertag[] {
  const land = useFeiertagsland();
  return useMemo(() => feiertageZwischen(von, bis, land), [von, bis, land]);
}

/**
 * Der leise Satz unter einem Zeitraum — „enthält Feiertag: Sa 03.10. Tag der
 * Deutschen Einheit", bei mehreren „enthält 2 Feiertage" mit den Namen im
 * `title`. Ohne Feiertag nichts. Mit `miettag` steht „zählt als Miettag" dabei.
 */
export function Feiertagshinweis({ von, bis, miettag = false, klasse = "" }: {
  von: string | null | undefined;
  bis: string | null | undefined;
  miettag?: boolean;
  klasse?: string;
}) {
  const t = useT();
  const liste = useFeiertageIm(von, bis);
  const satz = feiertagssatz(t, liste, miettag);
  if (!satz) return null;
  return (
    <span className={`feiertag-hinweis${klasse ? ` ${klasse}` : ""}`} title={satz.titel}>
      {satz.text}
    </span>
  );
}
